import { ComponentRef, Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, of, ReplaySubject } from 'rxjs';
import { BatchImportModel } from '@data-layer/models/common';
import { IsfBatchSidebarComponent } from '@shared/isf-mutual/components';
import { EsBatchSidebarComponent } from '@shared/es-mutual/components';
import { enumBatchImportItemType, enumBatchImportStatus } from '@data-layer/enums/common';
import { switchMap, takeUntil } from 'rxjs/operators';
import { AppService } from '@app/app.service';
import { Router } from '@angular/router';
import { enumCompanyPaymentType } from '@data-layer/enums/companies';
import { BillsRepository } from '@data-layer/repositories';
import { CompanyModel } from '@data-layer/models/companies';
import { BillItemPendingModel } from '@data-layer/models/payments';

@Injectable({ providedIn: 'root' })
export class BatchImportService implements OnDestroy {
  private batchImports$: BehaviorSubject<BatchImportModel[]> = new BehaviorSubject([]);
  private checkBatchImports$: BehaviorSubject<void> = new BehaviorSubject(null);
  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(private appService: AppService, private router: Router, private billsRepository: BillsRepository) {}

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public get getBatchImports$(): BehaviorSubject<BatchImportModel[]> {
    return this.batchImports$;
  }

  public setBatchImports(value: BatchImportModel[]): void {
    this.batchImports$.next(value);
  }

  public get getCheckBatchImports$(): BehaviorSubject<void> {
    return this.checkBatchImports$;
  }

  public setCheckBatchImports(): void {
    this.checkBatchImports$.next();
  }

  public openSidebar(batchItem: BatchImportModel): void {
    let componentSidebarRef: ComponentRef<IsfBatchSidebarComponent | EsBatchSidebarComponent>;

    if (batchItem.itemType === enumBatchImportItemType.ISF) {
      componentSidebarRef = this.appService.openSidebar(IsfBatchSidebarComponent, {
        sidebarHeaderText: 'ISF_BATCH_UPLOAD',
      });
      componentSidebarRef.instance.batchId = batchItem.id;
      componentSidebarRef.instance.onCreated.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.appService.closeSidebar();
      });
    } else {
      componentSidebarRef = this.appService.openSidebar(EsBatchSidebarComponent, {
        sidebarHeaderText: 'ES_BATCH_UPLOAD',
      });
      componentSidebarRef.instance.batchId = batchItem.id;
      componentSidebarRef.instance.onCreated.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.appService.closeSidebar();
      });
    }
  }

  /* ISF */
  public handleISFRedirect(
    batchId: number,
    referenceNumbers: string[],
    isConfirmed: boolean,
    currentBatchStatus: enumBatchImportStatus,
    company: CompanyModel,
  ): Observable<boolean> {
    return company.customsPaymentType === enumCompanyPaymentType.INSTANT
      ? this.handleISFInstantFlow(batchId, referenceNumbers, isConfirmed, currentBatchStatus)
      : this.handleISFMonthlyFlow(batchId, referenceNumbers, isConfirmed, currentBatchStatus, company.id);
  }

  public handleISFInstantFlow(
    batchId: number,
    referenceNumbers: string[],
    isConfirmed: boolean,
    currentBatchStatus: enumBatchImportStatus,
  ): Observable<boolean> {
    this.setBatchImports([]);
    this.setCheckBatchImports();

    if (isConfirmed && currentBatchStatus === enumBatchImportStatus.FINISHED) {
      this.router.navigate(['/', 'payment'], {
        queryParams: {
          referenceNumbers,
          batchId: batchId,
          returnUrl: `/isf?batchId=${batchId}`,
        },
      });
    } else {
      this.router.navigate(['/', 'isf', 'batch', batchId]);
    }

    return of(true);
  }

  public handleISFMonthlyFlow(
    batchId: number,
    referenceNumbers: string[],
    isConfirmed: boolean,
    currentBatchStatus: enumBatchImportStatus,
    companyId: number,
  ): Observable<boolean> {
    this.setCheckBatchImports();

    if (isConfirmed && currentBatchStatus === enumBatchImportStatus.FINISHED && !!referenceNumbers.length) {
      return this.billsRepository.pendingBills(companyId, { referenceNumbers, paymentProvider: null, paymentCodeId: null }).pipe(
        switchMap((bills: BillItemPendingModel[]) => {
          if (bills.some((billItem: BillItemPendingModel) => billItem.paymentType === enumCompanyPaymentType.INSTANT)) {
            this.router.navigate(['/', 'payment'], {
              queryParams: {
                referenceNumbers,
                batchId: batchId,
                returnUrl: `/isf?batchId=${batchId}`,
              },
            });
          } else {
            return this.billsRepository.create(companyId, { referenceNumbers, paymentProvider: null, paymentCodeId: null }).pipe(
              switchMap(() => {
                this.router.navigate(['/', 'isf'], { queryParams: { batchId } });

                return of(true);
              }),
            );
          }

          return of(true);
        }),
      );
    } else {
      this.router.navigate(['/', 'isf', 'batch', batchId]);
    }

    return of(true);
  }

  /* Entry Summary */
  public handleESRedirect(
    batchId: number,
    referenceNumbers: string[],
    isConfirmed: boolean,
    currentBatchStatus: enumBatchImportStatus,
    company: CompanyModel,
  ): Observable<boolean> {
    return company.customsPaymentType === enumCompanyPaymentType.INSTANT
      ? this.handleESInstantFlow(batchId, referenceNumbers, isConfirmed, currentBatchStatus)
      : this.handleESMonthlyFlow(batchId, referenceNumbers, isConfirmed, currentBatchStatus, company.id);
  }

  public handleESInstantFlow(
    batchId: number,
    referenceNumbers: string[],
    isConfirmed: boolean,
    currentBatchStatus: enumBatchImportStatus,
  ): Observable<boolean> {
    this.setBatchImports([]);
    this.setCheckBatchImports();

    if (isConfirmed && currentBatchStatus === enumBatchImportStatus.FINISHED) {
      this.router.navigate(['/', 'payment'], {
        queryParams: {
          referenceNumbers,
          batchId: batchId,
          returnUrl: `/entry-summary?batchId=${batchId}`,
        },
      });
    } else {
      this.router.navigate(['/', 'entry-summary', 'batch', batchId]);
    }

    return of(true);
  }

  public handleESMonthlyFlow(
    batchId: number,
    referenceNumbers: string[],
    isConfirmed: boolean,
    currentBatchStatus: enumBatchImportStatus,
    companyId: number,
  ): Observable<boolean> {
    this.setCheckBatchImports();

    if (isConfirmed && currentBatchStatus === enumBatchImportStatus.FINISHED && !!referenceNumbers.length) {
      return this.billsRepository.pendingBills(companyId, { referenceNumbers, paymentProvider: null, paymentCodeId: null }).pipe(
        switchMap((bills: BillItemPendingModel[]) => {
          if (bills.some((billItem: BillItemPendingModel) => billItem.paymentType === enumCompanyPaymentType.INSTANT)) {
            this.router.navigate(['/', 'payment'], {
              queryParams: {
                referenceNumbers,
                batchId: batchId,
                returnUrl: `/entry-summary?batchId=${batchId}`,
              },
            });
          } else {
            return this.billsRepository.create(companyId, { referenceNumbers, paymentProvider: null, paymentCodeId: null }).pipe(
              switchMap(() => {
                this.router.navigate(['/', 'entry-summary'], { queryParams: { batchId } });

                return of(true);
              }),
            );
          }

          return of(true);
        }),
      );
    } else {
      this.router.navigate(['/', 'entry-summary', 'batch', batchId]);
    }

    return of(true);
  }
}
